import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" }
const _hoisted_2 = { class: "border-b border-slate-200 px-4 py-4" }
const _hoisted_3 = { class: "flex items-start justify-between gap-3" }
const _hoisted_4 = { class: "text-xs font-semibold uppercase tracking-[0.18em] text-slate-500" }
const _hoisted_5 = { class: "mt-1 text-lg font-semibold text-slate-900" }
const _hoisted_6 = { class: "mt-1 text-sm text-slate-500" }
const _hoisted_7 = { class: "relative" }
const _hoisted_8 = {
  key: 0,
  class: "absolute inset-0 flex items-center justify-center bg-white/80"
}
const _hoisted_9 = {
  key: 1,
  class: "absolute inset-0 flex items-center justify-center bg-white/85 p-6"
}
const _hoisted_10 = { class: "max-w-xs text-center" }
const _hoisted_11 = { class: "mt-1 text-xs text-slate-500" }

import { computed, onBeforeUnmount, onMounted, ref, watch, type PropType } from 'vue'
import { useGoogleMaps } from '@/composables/useGoogleMaps'

type CompanyMapItem = {
  id?: number | string | null
  slug?: string | null
  name?: string | null
  office_address?: string | null
  website?: string | null
  market_cap?: number | string | null
  office_location_lat?: number | string | null
  office_location_lng?: number | string | null
}

type ProjectMapItem = {
  id: string
  name: string
  stage: string
  location_label?: string | null
  location_lat?: number | string | null
  location_lng?: number | string | null
  commodities?: string[]
  company_id?: number | string | null
  company_name?: string | null
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CompanyDirectoryMap',
  props: {
  mode: {
    type: String as PropType<'company' | 'project'>,
    default: 'company',
  },
  companies: {
    type: Array as PropType<CompanyMapItem[]>,
    default: () => [],
  },
  projects: {
    type: Array as PropType<ProjectMapItem[]>,
    default: () => [],
  },
  selectedId: {
    type: [String, Number],
    default: null,
  },
  mapHeightClass: {
    type: String,
    default: 'h-[560px]',
  },
},
  emits: ["marker-click"],
  setup(__props, { emit: __emit }) {

const props = __props

const emit = __emit

const mapContainer = ref<HTMLElement | null>(null)
const isLoading = ref(true)
const mappedCount = ref(0)

let map: any = null
let geocoder: any = null
let infoWindow: any = null
let bounds: any = null
let markers = new Map<string | number, any>()
let stylesInjected = false

const { loadScript } = useGoogleMaps()
const defaultCenter = { lat: -25.2744, lng: 133.7751 }

const geocodeCache = new Map<string, { lat: number; lng: number } | null>()
const geocodeRequests = new Map<string, Promise<{ lat: number; lng: number } | null>>()

const activeItems = computed(() => (props.mode === 'project' ? props.projects : props.companies))
const mapSubtitle = computed(() =>
  props.mode === 'project'
    ? `Showing mapped projects for ${props.projects.length} project result${props.projects.length === 1 ? '' : 's'}.`
    : `${mappedCount.value} compan${mappedCount.value === 1 ? 'y' : 'ies'} mapped (current results)`
)

const mapStyles = [
  { featureType: 'administrative', elementType: 'labels.text.fill', stylers: [{ color: '#484848' }] },
  { featureType: 'landscape', elementType: 'all', stylers: [{ color: '#f7f7f7' }] },
  { featureType: 'poi', elementType: 'all', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'all', stylers: [{ saturation: -100 }, { lightness: 45 }] },
  { featureType: 'transit', elementType: 'all', stylers: [{ visibility: 'simplified' }] },
  { featureType: 'water', elementType: 'all', stylers: [{ color: '#0ea5e9' }, { visibility: 'on' }, { lightness: 70 }] },
]

const parseCoordinate = (value: number | string | null | undefined): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number.parseFloat(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

const normalizeCompanyId = (company: CompanyMapItem): string | number | null =>
  company.id ?? company.slug ?? company.name ?? null

const normalizeAddress = (company: CompanyMapItem): string =>
  String(company.office_address ?? '').trim()

const stripProtocol = (website: string): string => website.replace(/^https?:\/\//i, '')

const formatMarketCapValue = (value: number | string | null | undefined): string => {
  if (typeof value === 'string' && value.trim().toUpperCase() === 'SUSPENDED') return 'SUSPENDED'
  if (typeof value !== 'number' || Number.isNaN(value)) return 'Private / Undisclosed'
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}b`
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(0)}m`
  return `$${value.toLocaleString()}`
}

const getCompanyCoordinates = (company: CompanyMapItem): { lat: number; lng: number } | null => {
  const lat = parseCoordinate(company.office_location_lat)
  const lng = parseCoordinate(company.office_location_lng)
  if (lat === null || lng === null) return null
  return { lat, lng }
}

const getProjectCoordinates = (project: ProjectMapItem): { lat: number; lng: number } | null => {
  const lat = parseCoordinate(project.location_lat)
  const lng = parseCoordinate(project.location_lng)
  if (lat === null || lng === null) return null
  return { lat, lng }
}

const getCompanyMarkerIcon = (active = false) => ({
  path: 'M12 2C7.9 2 4.5 5.4 4.5 9.5c0 5.2 7.5 11.5 7.5 11.5s7.5-6.3 7.5-11.5C19.5 5.4 16.1 2 12 2Z',
  fillOpacity: 1,
  fillColor: active ? '#0ea5e9' : '#0284c7',
  scale: active ? 1.7 : 1.45,
  strokeColor: active ? '#0ea5e9' : '#0369a1',
  strokeWeight: 1.6,
  anchor: new google.maps.Point(12, 24),
})

const getProjectMarkerIcon = (active = false) => ({
  path: 'M12 2C8.1 2 5 5.1 5 9c0 5.4 7 13 7 13s7-7.6 7-13c0-3.9-3.1-7-7-7Zm0 9.8a2.8 2.8 0 1 1 0-5.6 2.8 2.8 0 0 1 0 5.6Z',
  fillOpacity: 1,
  fillColor: active ? '#f59e0b' : '#d97706',
  scale: active ? 1.85 : 1.6,
  strokeColor: active ? '#f59e0b' : '#b45309',
  strokeWeight: 1.7,
  anchor: new google.maps.Point(12, 26),
})

const buildCompanyInfoWindowContent = (company: CompanyMapItem): string => {
  const name = String(company.name ?? 'Company')
  const officeAddress = normalizeAddress(company) || 'Office address not available'
  const website = typeof company.website === 'string' && company.website.trim() !== ''
    ? `<div style="font-size:12px;color:#0284c7;margin-top:4px;font-weight:600;">${stripProtocol(company.website)}</div>`
    : ''

  return `
    <div style="min-width:220px;font-family:Inter,system-ui,-apple-system,sans-serif;">
      <div style="font-size:14px;font-weight:700;color:#0f172a;line-height:1.2;">${name}</div>
      <div style="font-size:12px;color:#475569;margin-top:4px;">${officeAddress}</div>
      <div style="font-size:11px;color:#64748b;margin-top:6px;font-weight:600;">${formatMarketCapValue(company.market_cap)}</div>
      ${website}
    </div>
  `
}

const buildProjectInfoWindowContent = (project: ProjectMapItem): string => {
  const stage = project.stage ? `<div style="font-size:11px;color:#b45309;font-weight:700;margin-top:6px;">${project.stage}</div>` : ''
  const commodities = project.commodities?.length
    ? `<div style="font-size:11px;color:#64748b;margin-top:4px;">${project.commodities.join(', ')}</div>`
    : ''

  return `
    <div style="min-width:220px;font-family:Inter,system-ui,-apple-system,sans-serif;">
      <div style="font-size:14px;font-weight:700;color:#0f172a;line-height:1.2;">${project.name || 'Project'}</div>
      <div style="font-size:12px;color:#475569;margin-top:4px;">${project.company_name || 'Company'}</div>
      <div style="font-size:12px;color:#64748b;margin-top:4px;">${project.location_label || 'Location not provided'}</div>
      ${stage}
      ${commodities}
    </div>
  `
}

const injectInfoWindowStyles = () => {
  if (stylesInjected) return
  const style = document.createElement('style')
  style.id = 'company-directory-map-iw-style'
  style.textContent = `
    .gm-style .gm-style-iw-c {
      padding: 6px 10px 10px 10px !important;
      border-radius: 14px !important;
    }
    .gm-style .gm-style-iw-d {
      overflow: visible !important;
    }
  `
  document.head.appendChild(style)
  stylesInjected = true
}

const geocodeAddress = async (address: string): Promise<{ lat: number; lng: number } | null> => {
  if (!address || !geocoder) return null
  if (geocodeCache.has(address)) return geocodeCache.get(address) ?? null
  if (geocodeRequests.has(address)) return geocodeRequests.get(address) ?? null

  const request = new Promise<{ lat: number; lng: number } | null>((resolve) => {
    geocoder?.geocode({ address }, (results, status) => {
      if (status === 'OK' && results?.[0]?.geometry?.location) {
        const location = results[0].geometry.location
        const resolved = { lat: location.lat(), lng: location.lng() }
        geocodeCache.set(address, resolved)
        resolve(resolved)
        return
      }

      geocodeCache.set(address, null)
      resolve(null)
    })
  })

  geocodeRequests.set(address, request)
  const result = await request
  geocodeRequests.delete(address)
  return result
}

const resolveCompanyPosition = async (company: CompanyMapItem): Promise<{ lat: number; lng: number } | null> => {
  const explicit = getCompanyCoordinates(company)
  if (explicit) return explicit

  const address = normalizeAddress(company)
  if (!address) return null

  return geocodeAddress(address)
}

const updateMarkers = async () => {
  if (!map || typeof google === 'undefined') return

  isLoading.value = true
  bounds = new google.maps.LatLngBounds()
  const seen = new Set<string | number>()
  let mapped = 0

  if (props.mode === 'project') {
    for (const project of props.projects) {
      const position = getProjectCoordinates(project)
      if (!position) continue

      seen.add(project.id)
      mapped += 1

      let marker = markers.get(project.id)
      if (!marker) {
        marker = new google.maps.Marker({
          position,
          map,
          icon: getProjectMarkerIcon(project.id === props.selectedId),
          optimized: true,
        })
        marker.addListener('click', () => {
          emit('marker-click', project.id ?? null)
          infoWindow?.setContent(buildProjectInfoWindowContent(project))
          infoWindow?.open({ map, anchor: marker, shouldFocus: false })
        })
        markers.set(project.id, marker)
      } else {
        marker.setPosition(position)
      }

      marker.setIcon(getProjectMarkerIcon(project.id === props.selectedId))
      bounds.extend(position)
    }
  } else {
    for (const company of props.companies) {
      const id = normalizeCompanyId(company)
      if (id === null) continue

      const position = await resolveCompanyPosition(company)
      if (!position) continue

      seen.add(id)
      mapped += 1

      let marker = markers.get(id)
      if (!marker) {
        marker = new google.maps.Marker({
          position,
          map,
          icon: getCompanyMarkerIcon(id === props.selectedId),
          optimized: true,
        })
        marker.addListener('click', () => {
          emit('marker-click', id)
          infoWindow?.setContent(buildCompanyInfoWindowContent(company))
          infoWindow?.open({ map, anchor: marker, shouldFocus: false })
        })
        markers.set(id, marker)
      } else {
        marker.setPosition(position)
      }

      marker.setIcon(getCompanyMarkerIcon(id === props.selectedId))
      bounds.extend(position)
    }
  }

  markers.forEach((marker, id) => {
    if (!seen.has(id)) {
      marker.setMap(null)
      markers.delete(id)
    }
  })

  mappedCount.value = mapped

  if (mapped > 0) {
    map.fitBounds(bounds)
    google.maps.event.addListenerOnce(map, 'idle', () => {
      const zoom = map?.getZoom()
      if (zoom && zoom > 13) {
        map?.setZoom(13)
      }
    })
  } else {
    map.setCenter(defaultCenter)
    map.setZoom(4)
  }

  isLoading.value = false
}

const highlightSelected = () => {
  if (props.mode === 'project') {
    props.projects.forEach((project) => {
      const marker = markers.get(project.id)
      if (marker) {
        marker.setIcon(getProjectMarkerIcon(project.id === props.selectedId))
      }
    })
    return
  }

  props.companies.forEach((company) => {
    const id = normalizeCompanyId(company)
    if (id === null) return
    const marker = markers.get(id)
    if (marker) {
      marker.setIcon(getCompanyMarkerIcon(id === props.selectedId))
    }
  })
}

const initMap = async () => {
  if (!mapContainer.value) return

  try {
    await loadScript()
    geocoder = new google.maps.Geocoder()
    map = new google.maps.Map(mapContainer.value, {
      center: defaultCenter,
      zoom: 4,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_TOP,
      },
      styles: mapStyles,
    })

    infoWindow = new google.maps.InfoWindow({ maxWidth: 300 })
    injectInfoWindowStyles()
    await updateMarkers()
  } catch (error) {
    console.error('Failed to initialize company directory map:', error)
    isLoading.value = false
  }
}

watch(
  () => [props.mode, props.companies, props.projects],
  async () => {
    if (!map) return
    await updateMarkers()
  },
  { deep: true }
)

watch(
  () => props.selectedId,
  () => {
    highlightSelected()
  }
)

onMounted(() => {
  initMap()
})

onBeforeUnmount(() => {
  markers.forEach((marker) => marker.setMap(null))
  markers.clear()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", null, [
          _createElementVNode("p", _hoisted_4, _toDisplayString(__props.mode === 'project' ? 'Project map' : 'Company map'), 1),
          _createElementVNode("h3", _hoisted_5, _toDisplayString(__props.mode === 'project' ? 'Project locations' : 'Head Office locations'), 1),
          _createElementVNode("p", _hoisted_6, _toDisplayString(mapSubtitle.value), 1)
        ]),
        _createElementVNode("span", {
          class: _normalizeClass(["inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1", __props.mode === 'project'
            ? 'bg-amber-50 text-amber-700 ring-amber-100'
            : 'bg-sky-50 text-sky-700 ring-sky-100'])
        }, _toDisplayString(mappedCount.value) + " mapped ", 3)
      ])
    ]),
    _createElementVNode("div", _hoisted_7, [
      _createElementVNode("div", {
        ref_key: "mapContainer",
        ref: mapContainer,
        class: _normalizeClass(['w-full', __props.mapHeightClass])
      }, null, 2),
      (isLoading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, _cache[0] || (_cache[0] = [
            _createElementVNode("div", { class: "flex items-center gap-3 text-sm text-slate-600" }, [
              _createElementVNode("div", { class: "h-6 w-6 animate-spin rounded-full border-2 border-sky-500 border-t-transparent" }),
              _createElementVNode("span", null, "Loading map...")
            ], -1)
          ])))
        : (activeItems.value.length > 0 && mappedCount.value === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
              _createElementVNode("div", _hoisted_10, [
                _cache[1] || (_cache[1] = _createElementVNode("p", { class: "text-sm font-semibold text-slate-900" }, "No mappable locations yet", -1)),
                _createElementVNode("p", _hoisted_11, _toDisplayString(__props.mode === 'project'
              ? 'These projects do not currently have usable coordinates.'
              : 'These companies do not currently resolve to usable office coordinates.'), 1)
              ])
            ]))
          : _createCommentVNode("", true)
    ])
  ]))
}
}

})